import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { IMAGES } from "@/lib/assets";
import { useSettings } from "@/lib/SettingsContext";

const PRIVACY = {
  title: "Privacy Policy",
  intro:
    "The National Transportation Operators Association (\u201cNTOA\u201d, \u201cwe\u201d, \u201cus\u201d) respects your privacy. This policy explains what information we collect, how we use it, and the choices you have.",
  sections: [
    ["Information We Collect", "We collect information you provide directly \u2014 such as your name, email, phone number, and company details when you join, contact us, or enter a promotion \u2014 and basic usage data (pages visited, device/browser) collected automatically to improve the site."],
    ["How We Use Information", "To operate and improve NTOA, process memberships, respond to inquiries, run promotions and giveaways, send occasional updates you\u2019ve opted into, and comply with legal obligations."],
    ["Sharing", "We do not sell your personal information. We share it only with trusted partners who help us operate (for example, our membership platform Zion Underwriting) and when required by law."],
    ["Cookies", "We use minimal cookies and local storage to remember preferences and measure basic traffic. You can control cookies through your browser settings."],
    ["Your Choices", "You may request access to, correction of, or deletion of your personal information, and opt out of marketing messages at any time by emailing us."],
    ["Data Security", "We use reasonable administrative and technical safeguards to protect your information. No method of transmission is 100% secure."],
    ["Contact", "Questions about this policy? Email info@ntoassoc.com."],
  ],
};

const TERMS = {
  title: "Terms of Service",
  intro:
    "These Terms govern your use of the NTOA website and membership. By using this site or joining NTOA, you agree to these Terms.",
  sections: [
    ["Membership", "NTOA memberships are offered directly and through Zion Underwriting. Pricing, benefits, and availability may change. Some benefits shown may be marked \u201ccoming soon\u201d and are not yet active."],
    ["Use of the Site", "You agree to use the site lawfully and not to misuse, disrupt, or attempt to gain unauthorized access to any part of it."],
    ["Promotions & Giveaways", "Promotions, giveaways, and discounts are subject to their stated start and end dates and any additional rules posted at the time. No purchase is necessary where prohibited by law."],
    ["Third-Party Links", "The site links to third-party services (such as Zion Underwriting). We are not responsible for the content or practices of those services."],
    ["Disclaimers", "The site and membership are provided \u201cas is\u201d without warranties of any kind to the fullest extent permitted by law."],
    ["Limitation of Liability", "To the maximum extent permitted by law, NTOA is not liable for indirect or consequential damages arising from your use of the site or membership."],
    ["Changes", "We may update these Terms from time to time. Continued use after changes constitutes acceptance."],
    ["Contact", "Questions about these Terms? Email info@ntoassoc.com."],
  ],
};

export const PolicyPage = ({ type }) => {
  const settings = useSettings();
  const data = type === "terms" ? TERMS : PRIVACY;
  const brand = settings.brand;
  const legal = settings.legal || {};
  const isBylaws = type === "bylaws";

  return (
    <div className="min-h-screen bg-white text-navy-dark grain">
      <header className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="policy-home-link">
            <img src={IMAGES.logo} alt="NTOA" className="h-10 w-10 object-contain" />
            <span className="font-display font-black tracking-tight">{brand?.name || "NTOA"}</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-400 hover:text-amber-500 transition-colors" data-testid="policy-back-link">
            <ArrowLeft size={16} /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <p className="text-xs tracking-[0.3em] uppercase text-amber-500 font-bold mb-4">
          {isBylaws ? "Who We Are" : "Legal"}
        </p>
        <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl md:text-5xl leading-[0.95]" data-testid="policy-title">
          {isBylaws ? "Purpose & Bylaws" : data.title}
        </h1>
        <p className="mt-3 text-sm text-zinc-500">Last updated: June 2026</p>

        {isBylaws ? (
          <div className="mt-10 space-y-10">
            <section data-testid="bylaws-purpose">
              <h2 className="font-display font-bold text-xl text-navy-dark">Our Purpose</h2>
              <p className="mt-3 text-lg text-zinc-400 leading-relaxed">{legal.purpose}</p>
            </section>
            <section data-testid="bylaws-body">
              <h2 className="font-display font-bold text-xl text-navy-dark mb-3">Bylaws</h2>
              <div className="text-base text-zinc-400 leading-relaxed whitespace-pre-line">{legal.bylaws}</div>
            </section>
          </div>
        ) : (
          <>
            <p className="mt-8 text-base text-zinc-400 leading-relaxed">{data.intro}</p>
            <div className="mt-10 space-y-8">
              {data.sections.map(([heading, body], i) => (
                <section key={i}>
                  <h2 className="font-display font-bold text-xl text-navy-dark">{i + 1}. {heading}</h2>
                  <p className="mt-2 text-base text-zinc-400 leading-relaxed">{body}</p>
                </section>
              ))}
            </div>
          </>
        )}

        <p className="mt-14 text-xs text-zinc-500">
          This document is provided for general information and is not legal advice. Please review with your own counsel before publishing.
        </p>
      </main>
    </div>
  );
};
